import { Box } from "@mui/material"
import type { ColumnDef } from "@tanstack/react-table"
import { twMerge } from "tailwind-merge"

import WarehouseShelvingRowActions from "../components/WarehouseShelvingTable/WarehouseShelvingRowActions"
import {
  getWarehouseShelvingBinSizeLabel,
  getWarehouseShelvingBinStatusColor,
  getWarehouseShelvingBinStatusLabel,
} from "../utils"

import StyledChip from "@/components/common/Chip/Chip"
import { PermissionActionType } from "@/constants/permissions"
import { type WarehouseShelving } from "@/graphql/codegen/graphql"
import { usePermissionManager } from "@/hooks/usePermissionManager"
import { booleanToString } from "@/utils/utils"

export const useWarehouseShelvingColumns = (onRefresh: () => void): ColumnDef<WarehouseShelving>[] => {
  const { hasPermissions } = usePermissionManager()

  return [
    {
      accessorKey: "name",
      header: "Location",
      sortingFn: "alphanumeric",
      enableSorting: true,
      cell: ({ row }) => row.original.name,
    },
    {
      accessorKey: "aisleCode",
      header: "Aisle",
      sortingFn: "alphanumeric",
      enableSorting: true,
      size: 100,
      cell: ({ row }) => row.original.aisleCode ?? "",
    },
    {
      accessorKey: "sectionCode",
      header: "Section",
      sortingFn: "alphanumeric",
      enableSorting: true,
      size: 100,
      cell: ({ row }) => row.original.sectionCode ?? "",
    },
    {
      accessorKey: "binNumber",
      header: "Bin",
      sortingFn: "alphanumeric",
      enableSorting: true,
      size: 100,
      cell: ({ row }) => row.original.binNumber ?? "",
    },
    {
      accessorKey: "coldStorage",
      header: "Cold Storage",
      sortingFn: "alphanumeric",
      enableSorting: true,
      size: 100,
      cell: ({ row }) => booleanToString(row.original.coldStorage),
    },

    {
      accessorKey: "h3ColdStorage",
      header: "H3 Storage",
      sortingFn: "alphanumeric",
      enableSorting: true,
      size: 100,
      cell: ({ row }) => booleanToString(row.original.h3ColdStorage),
    },

    {
      accessorKey: "binSize",
      header: "Bin Size",
      sortingFn: "alphanumeric",
      enableSorting: true,
      size: 120,
      cell: ({ row }) => (row.original.binSize && getWarehouseShelvingBinSizeLabel(row.original.binSize)) || "",
    },
    {
      accessorKey: "deep",
      header: "Deep",
      sortingFn: "alphanumeric",
      enableSorting: true,
      size: 100,
      cell: ({ row }) => booleanToString(row.original.deep),
    },
    {
      accessorKey: "binStatus",
      header: "Bin Status",
      sortingFn: "alphanumeric",
      enableSorting: true,
      size: 120,
      cell: ({ row, cell, getValue }) => (
        <Box>
          <StyledChip
            size='small'
            className={twMerge("rounded-s border-none text-xs text-gray-800")}
            variant='outlined'
            color={getWarehouseShelvingBinStatusColor(
              !!row.original.isAvailable,
              !!row.original.isDoNotUse,
              !!row.original.isAllocated
            )}
            label={getWarehouseShelvingBinStatusLabel(
              !!row.original.isAvailable,
              !!row.original.isDoNotUse,
              !!row.original.isAllocated
            )}
            data-test={`${cell.id}-${getValue()}`}
          />
        </Box>
      ),
    },
    ...(hasPermissions([PermissionActionType.WAREHOUSES_MAINTENANCE])
      ? [
          {
            accessorKey: "actions",
            header: "",
            enableSorting: false,
            size: 72,
            cell: ({ row }) => <WarehouseShelvingRowActions row={row} onRefresh={onRefresh} />,
          } as ColumnDef<WarehouseShelving>,
        ]
      : []),
  ]
}
